import { useEffect, useState } from 'react';
import { observer } from "mobx-react-lite";
import { Link, useSearchParams } from 'react-router-dom';

import dayjs from 'dayjs';

import Stack from '@mui/joy/Stack';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import { Option, Select, Table } from '@mui/joy';
import Skeleton from '@mui/joy/Skeleton';
import Typography from '@mui/joy/Typography';

import useDedicatedAssignment from 'hooks/useDedicatedAssignment';
import useWorkKey from 'hooks/useWorkKey';
import useIdentity from 'hooks/useIdentity';
import useCurrentIdentity from 'hooks/useCurrentIdentity';
import RoleStack from './RoleStack';
import AssistantOnly from 'components/AssistantOnly';
import IdentityAvatar from './IdentityAvatar';
import FancyIcon from './FancyIcon';
import { DurationSeconds, TimeUI } from './Date';


const DedicatedTableRow = observer(({ identityKey, index }: { identityKey: string, index: number }) => {
    const { getIdentity } = useIdentity();
    const identity = getIdentity(identityKey);
    const { items} = useDedicatedAssignment();

    const end = identity?.next_billing_cycle_date ? dayjs(identity.next_billing_cycle_date) : '';
    const start = identity?.next_billing_cycle_date ? dayjs(identity.next_billing_cycle_date).subtract(1, "month") : '';

    return (

        <tr >

        <td>
        <Stack direction="row" spacing={1}>
        <Typography sx={{ width: '20px'}}>
        {index + 1}
        </Typography>
            <IdentityAvatar size="inline" identityKey={identityKey} />
            </Stack>
            </td>
            <td>
                <Stack spacing={0.5}>
                    {items.filter((i) => i.identity === identityKey).map((i) => {
                        return (
                            <IdentityAvatar key={i.identity} size="inline" identityKey={i.assistant_identity} />
                        );
                    })}
                </Stack>
            </td>
            <td>
            { identity ? <TimeUI ts={identity.next_billing_cycle_date} /> : <Skeleton variant="text" width={100} /> }
            </td>

            <td>
                {
                    identity ? <DurationSeconds seconds={identity.credit_included * 60} /> :
                        <Skeleton variant="text" width={100} />
                }
                </td>
                <td>
                {
                    identity ? <DurationSeconds seconds={identity.current_credit * 60} />
                        : <Skeleton variant="text" width={100} />
                }

            </td>

            <td>
            <Link to={`/identity/${identityKey}/clock-report?period_start=${start}&period_end=${end}`}>
                    <Button size="sm" variant="plain">Report</Button>
                </Link>
            </td>
        </tr>


    );
});

const DedicatedDashboard = observer(() => {

    const { fetchAll, items} = useDedicatedAssignment();
    const [searchParams, setSearchParams] = useSearchParams();
    const workKey = useWorkKey();
    const { getIdentity, fetchIdentity, identities, fetchListByDedicated } = useIdentity();
    const currentIdentity = useCurrentIdentity();
    const [clientAscending, setClientAscending] = useState(true);
    const [assistantFilter, setAssistantFilter] = useState(searchParams.get("assistant") || "");

    useEffect(() => {
        document.title = "Dedicated Dashboard";
    }, []);

    useEffect(() => {
        if(workKey) {
            fetchAll(workKey);
            fetchListByDedicated(currentIdentity.uuid);
        }
    }, [workKey]);

    //console.log("dedicated_identities:", items);
    const ourItems = assistantFilter ? items.filter((i) => i.assistant_identity === assistantFilter) : items;
    const dedicated_identities = [...new Set(ourItems.map((d) => d.identity))];

    const sortCmp = (b: string, a: string) => {
        const ia = getIdentity(a);
        const ib = getIdentity(b);
        if(ia && ib) {
            // console.log('comparing', ia.name, ib.name);
            if(clientAscending) {
                return ib.name.localeCompare(ia.name);
            }
            else {
                return ia.name.localeCompare(ib.name);

            }
        }
        if(!ia) {
            fetchIdentity(currentIdentity.uuid, a);
        }
        if(!ib) {
            fetchIdentity(currentIdentity.uuid, b);
        }
        // console.log("don't have a,b", ia, ib);
        return 0;
    };

    return (
        <AssistantOnly>
        <RoleStack role="support">

            <Select
                defaultValue={assistantFilter}
                onChange={(_, v) => {
                    setSearchParams({ assistant: v as string});
                    setAssistantFilter(v as string);
                }}

                renderValue={(selected) => {
                    if (!selected || selected.value == '') return <Typography>👬 Everybody</Typography>;
                    else {
                        return <IdentityAvatar identityKey={selected?.value.toString()} size="inline" interactive={false} />
                    }
                }}
            >
                <Option value={''}>👬 Everybody</Option>
                {identities.filter((m) => m.team === currentIdentity.team && m.dedicated).map((m) => {
                    return (
                        <Option key={m.uuid} value={m.uuid}>
                            <IdentityAvatar identityKey={m.uuid} size="inline" interactive={false} />
                        </Option>
                        );
                    })}
                </Select>

                <Table>
                    <thead>
                        <tr>
                            <th>

                                <Typography
                                    onClick={() => setClientAscending(!clientAscending)}
                                    sx={{ cursor: "pointer" }}
                                    endDecorator={
                                        <IconButton size="sm"
                                            onClick={() => setClientAscending(!clientAscending)}
                                            variant="plain" >

                                            <FancyIcon size="sm" name="ArrowUpward"
                                                sx={{
                                                    transition: "transform 0.3s",
                                                    transform: clientAscending ? "rotate(180deg)" : "rotate(0deg)"
                                                }}
                                            />

                                        </IconButton>

                                    }>
                                    Client
                                </Typography>
                            </th>
                            <th>Assistants</th>
                            <th>Renewal Date</th>
                            <th>Hours Included</th>
                            <th>Remaining</th>
                            <th>Report</th>
                        </tr>
                    </thead>
                    <tbody>
                    {[...dedicated_identities].sort(sortCmp).map((d, index) =>
                        <DedicatedTableRow key={d} index={index} identityKey={d} />
                        )}
                    </tbody>
                </Table>
            </RoleStack>
        </AssistantOnly>
    );
});

export default DedicatedDashboard;
